import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABEL } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { HardDrive, Users, LogOut, CheckCircle2, XCircle, Plus, Wallet, Receipt, KeyRound } from "lucide-react";
import { ChangePasswordDialog } from "../components/ChangePasswordDialog";

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [drive, setDrive] = useState({ configured: false, connected: false });
  const [users, setUsers] = useState([]);
  const [showUser, setShowUser] = useState(false);
  const [nf, setNf] = useState({ name: "", email: "", password: "", role: "accountant", is_active: true });
  const [mig, setMig] = useState(null);
  const [showCp, setShowCp] = useState(false);

  const loadDrive = useCallback(() => api.get("/drive/status").then(({ data }) => setDrive(data)).catch(() => {}), []);
  const loadMig = useCallback(() => api.get("/drive/migration-status").then(({ data }) => setMig(data)).catch(() => {}), []);
  const loadUsers = useCallback(() => { if (user?.role === "admin") api.get("/users").then(({ data }) => setUsers(data)).catch(() => {}); }, [user]);
  useEffect(() => { loadDrive(); loadUsers(); loadMig(); }, [loadDrive, loadUsers, loadMig]);

  const refreshDrive = async () => { try { await api.get("/drive/refresh"); toast.success("Bağlantı yenilendi"); loadDrive(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const migrateLocal = async () => { try { const { data } = await api.post("/drive/migrate-local"); toast.success(`Aktarım: ${data.success} başarılı, ${data.failed} başarısız, ${data.pending} bekleyen`); loadMig(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const resetUserPassword = async (u) => { const np = window.prompt(`${u.name} için yeni şifre (en az 6 karakter):`); if (!np) return; try { await api.post(`/users/${u.id}/reset-password`, { new_password: np }); toast.success("Şifre sıfırlandı"); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  useEffect(() => {
    if (params.get("drive") === "connected") { toast.success("Google Drive bağlandı"); loadDrive(); setParams({}); }
    if (params.get("drive") === "error") { toast.error("Google Drive bağlantısı başarısız"); setParams({}); }
  }, [params, setParams, loadDrive]);

  const connectDrive = async () => {
    try {
      const { data } = await api.get("/drive/connect");
      window.location.href = data.authorization_url;
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const disconnectDrive = async () => {
    try { await api.post("/drive/disconnect"); toast.success("Bağlantı kaldırıldı"); loadDrive(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const createUser = async () => {
    try { await api.post("/users", nf); toast.success("Kullanıcı oluşturuldu"); setShowUser(false); setNf({ name: "", email: "", password: "", role: "accountant", is_active: true }); loadUsers(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const toggleActive = async (u) => {
    try { await api.put(`/users/${u.id}`, { is_active: !u.is_active }); loadUsers(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-in-up max-w-2xl">
      <h1 className="font-heading text-2xl md:text-3xl font-bold text-navy-900">Ayarlar</h1>

      {/* Account */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5">
        <h2 className="font-heading font-semibold text-navy-900 mb-3">Hesap</h2>
        <p className="text-sm text-navy-900 font-medium">{user?.name}</p>
        <p className="text-sm text-slate-400">{user?.email} · {ROLE_LABEL[user?.role]}</p>
        <div className="flex flex-wrap gap-2 mt-4 md:hidden">
          <Button variant="outline" className="rounded-xl" onClick={() => navigate("/odemeler")}><Wallet className="w-4 h-4 mr-1" />Ödemeler</Button>
          <Button variant="outline" className="rounded-xl" onClick={() => navigate("/raporlar")}><Receipt className="w-4 h-4 mr-1" />Raporlar</Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <Button variant="outline" className="rounded-xl" data-testid="change-password-btn" onClick={() => setShowCp(true)}>
            <KeyRound className="w-4 h-4 mr-1" /> Şifre Değiştir
          </Button>
          <Button variant="outline" className="rounded-xl text-brand-red border-brand-redbg" data-testid="settings-logout-btn" onClick={logout}>
            <LogOut className="w-4 h-4 mr-1" /> Çıkış Yap
          </Button>
        </div>
        <ChangePasswordDialog open={showCp} onClose={() => setShowCp(false)} />
      </section>

      {/* Google Drive */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-3">
          <HardDrive className="w-5 h-5 text-navy-900" />
          <h2 className="font-heading font-semibold text-navy-900">Google Drive</h2>
        </div>
        <div className="flex items-center gap-2 mb-3">
          {drive.connected
            ? <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600"><CheckCircle2 className="w-4 h-4" /> Bağlı</span>
            : <span className="inline-flex items-center gap-1.5 text-sm text-slate-400"><XCircle className="w-4 h-4" /> Bağlı değil</span>}
        </div>
        {drive.connected && (
          <div className="text-sm text-slate-600 space-y-1 mb-4 bg-slate-50 rounded-xl p-3">
            <p>Bağlı Hesap: <span className="font-medium text-navy-900">{drive.account_email || "—"}</span></p>
            <p>Ana Klasör: {drive.root_link ? <a href={drive.root_link} target="_blank" rel="noreferrer" className="text-brand-red underline">SHB Finans</a> : "SHB Finans"}</p>
            <p className="text-xs text-slate-400">Son bağlantı: {drive.last_connected_at ? new Date(drive.last_connected_at).toLocaleString("tr-TR") : "—"}</p>
          </div>
        )}
        {!drive.configured && (
          <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mb-3">
            Google Drive henüz yapılandırılmadı. Yönetici, sunucuda Client ID/Secret tanımladıktan sonra bağlanabilir. O zamana kadar belgeler yerel depolamada güvenle saklanır.
          </p>
        )}
        {user?.role === "admin" && (
          <div className="flex flex-wrap gap-2">
            {drive.connected ? (
              <>
                <Button variant="outline" className="rounded-xl" data-testid="drive-refresh-btn" onClick={refreshDrive}>Bağlantıyı Yenile</Button>
                <Button variant="outline" className="rounded-xl text-brand-red" data-testid="drive-disconnect-btn" onClick={disconnectDrive}>Bağlantıyı Kes</Button>
                <Button className="rounded-xl bg-navy-900 text-white" data-testid="drive-migrate-btn" onClick={migrateLocal}>Yerel Belgeleri Drive'a Aktar</Button>
              </>
            ) : (
              <Button className="rounded-xl bg-navy-900 text-white" data-testid="drive-connect-btn" disabled={!drive.configured} onClick={connectDrive}>Google Drive'a Bağlan</Button>
            )}
          </div>
        )}
        {mig && (
          <div className="mt-3 text-xs text-slate-500 flex flex-wrap gap-3">
            <span>Yerel: {mig.LOCAL ?? 0}</span>
            <span>Drive: {mig.DRIVE ?? 0}</span>
            <span className="text-amber-700">Bekleyen: {(mig.DRIVE_UPLOAD_PENDING ?? 0) + (mig.MIGRATION_FAILED ?? 0)}</span>
          </div>
        )}
      </section>

      {/* Users (admin) */}
      {user?.role === "admin" && (
        <section className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><Users className="w-5 h-5 text-navy-900" /><h2 className="font-heading font-semibold text-navy-900">Kullanıcılar</h2></div>
            <Button size="sm" className="rounded-xl bg-navy-900 text-white" data-testid="add-user-btn" onClick={() => setShowUser(true)}><Plus className="w-4 h-4 mr-1" />Ekle</Button>
          </div>
          <div className="divide-y divide-slate-100">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-navy-900">{u.name}</p>
                  <p className="text-xs text-slate-400">{u.email} · {ROLE_LABEL[u.role]}</p>
                </div>
                <div className="flex items-center gap-3">
                  <button data-testid={`reset-pw-${u.id}`} onClick={() => resetUserPassword(u)} className="text-slate-400 hover:text-navy-900 transition-colors" title="Şifre Sıfırla"><KeyRound className="w-4 h-4" /></button>
                  <Switch checked={u.is_active} onCheckedChange={() => toggleActive(u)} disabled={u.email === user.email} />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <Dialog open={showUser} onOpenChange={setShowUser}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading text-navy-900">Yeni Kullanıcı</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs uppercase text-slate-500">Ad Soyad</Label><Input data-testid="user-name-input" value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase text-slate-500">E-posta</Label><Input data-testid="user-email-input" type="email" value={nf.email} onChange={(e) => setNf({ ...nf, email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase text-slate-500">Şifre</Label><Input data-testid="user-password-input" type="password" value={nf.password} onChange={(e) => setNf({ ...nf, password: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase text-slate-500">Rol</Label>
              <Select value={nf.role} onValueChange={(v) => setNf({ ...nf, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Yönetici</SelectItem>
                  <SelectItem value="lawyer">Avukat</SelectItem>
                  <SelectItem value="accountant">Muhasebe</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setShowUser(false)}>İptal</Button>
            <Button className="rounded-xl bg-navy-900 text-white" data-testid="user-submit-btn" onClick={createUser}>Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
